/*******************************************************************************
 * Dependency Imports
 ******************************************************************************/
// auth::api
import * as Api from './api'
// lodash - https://lodash.com/
import _ from 'lodash'

/*******************************************************************************
 * Services
 ******************************************************************************/

/*******************************************************************************
 * Configuration
 ******************************************************************************/

/*******************************************************************************
 * Transformer prototypes
 ******************************************************************************/

/*******************************************************************************
 * Private methods
 ******************************************************************************/

/**
 * general success
 *
 * @param {obj} data
 * @return {obj} le data
 */
const _onSuccess = data => {

    if( _.get( data, 'data.data' ) ) {
        return _.get( data, 'data.data' )
    }

    return data

}

/**
 * general fail
 *
 * @param {obj} error from a server
 * @return {obj} the error
 */
const _onFailed = error => new Promise(( resolve, reject ) => {

    let error_obj = error.hasOwnProperty( 'response' ) ? error.response : error

    reject( _.get( error_obj, 'data', null ) || error_obj )

})

/*******************************************************************************
 * Public methods
 ******************************************************************************/

/**
 * create a new sale type
 *
 * @return {promise}
 */
export const createSaleType = async (data) =>
    Api.createSaleType(data)
	    .then( _onSuccess )
        .catch( _onFailed )

/**
 * update a sale type
 * @param sale_type_id
 * @param data
 * @returns {Promise<obj>}
 */
export const updateSaleType = async (sale_type_id, data) =>
    Api.updateSaleType(sale_type_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * add a new label to an existing sale type
 */
export const addLabelToSaleType = async (sale_type_id = null, data = null) =>
    Api.addLabelToSaleType(sale_type_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale types for a given auctioneer
 */
export const getAuctioneerSaleTypes = async auctioneer_id =>
    Api.getAuctioneerSaleTypes(auctioneer_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale codes for a given auctioneer
 */
export const getAuctioneerSaleCodes = async auctioneer_id =>
    Api.getAuctioneerSaleCodes(auctioneer_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale sections for a given organisation
 */
export const getAuctioneerSaleSections = async auctioneer_id =>
    Api.getAuctioneerSaleSections(auctioneer_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * create a new sale section
 *
 * @return {promise}
 */
export const createSaleSection = async (data) =>
    Api.createSaleSection(data)
	    .then( _onSuccess )
        .catch( _onFailed )

/**
 * update a sale secrtion
 */
export const updateSaleSection = async (sale_section_id, data) =>
    Api.updateSaleSection(sale_section_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * create a new sale code
 *
 * @return {promise}
 */
export const createSaleCode = async (data) =>
    Api.createSaleCode(data)
	    .then( _onSuccess )
        .catch( _onFailed )

/**
 * attach sale types to a sale section
 */
export const attachSaleTypesToSaleSection = async (sale_section_id, data) =>
    Api.attachSaleTypesToSaleSection(sale_section_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * sync sale types to a sale section
 */
export const syncSaleTypesToSaleSection = async (sale_section_id, data) =>
    Api.syncSaleTypesToSaleSection(sale_section_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * attach sale codes to a sale section
 */
export const attachSaleCodesToSaleSection = async (sale_section_id, data) =>
    Api.attachSaleCodesToSaleSection(sale_section_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * sync sale codes to a sale section
 */
export const syncSaleCodesToSaleSection = async (sale_section_id, data) =>
    Api.syncSaleCodesToSaleSection(sale_section_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * attach sale sections to a sale type
 */
export const attachSaleSectionsToSaleType = async (sale_type_id, data) =>
    Api.attachSaleSectionsToSaleType(sale_type_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * sync sale sections o sale type
 */
export const syncSaleSectionsToSaleType = async (sale_type_id, data) =>
    Api.syncSaleSectionsToSaleType(sale_type_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * attach sale sections to a sale code
 * @param {*} sale_code_id 
 * @param {*} data 
 * @returns 
 */
export const attachSaleSectionsToSaleCode = async (sale_code_id, data) =>
    Api.attachSaleSectionsToSaleCode(sale_code_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * attach feature field to a sale code
 */
export const attachFeatureFieldToSaleCode = async (sale_code_id, data) =>
    Api.attachFeatureFieldToSaleCode(sale_code_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale types for a user
 * @param {*} user_id 
 * @param {*} params 
 * @returns 
 */
export const getUserSaleTypes = async (user_id, params = []) =>
    Api.getUserSaleTypes(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale sections for a user
 * @param {*} user_id 
 * @param {*} params 
 * @returns 
 */
export const getUserSaleSections = async (user_id, params = []) =>
    Api.getUserSaleSections(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale codes for a user
 * @param {*} user_id 
 * @param {*} params 
 * @returns 
 */
export const getUserSaleCodes = async (user_id, params = []) =>
    Api.getUserSaleCodes(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a sale type by id
 */
export const getSaleTypeById = async sale_type_id =>
    Api.getSaleTypeById(sale_type_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a sale section by id
 */
export const getSaleSectionById = async sale_section_id =>
    Api.getSaleSectionById(sale_section_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a sale code by id
 */
export const getSaleCodeById = async sale_code_id =>
    Api.getSaleCodeById(sale_code_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * update a sale code
 */
export const updateSaleCode = async (sale_code_id, payload) =>
    Api.updateSaleCode(sale_code_id, payload)
        .then( _onSuccess )
        .catch( _onFailed )
/**
 * create a draft
 */
export const createDraftSale = async data =>
    Api.createDraftSale(data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a draft sale that's in the process of being configured
 */
export const getDraftSale = async sale_id =>
    Api.getDraftSale(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * set the times on a draft sale
 */
export const setDraftSaleTimes = async (sale_id, data) =>
    Api.setDraftSaleTimes(sale_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

export const exportInfomart = async sale_id =>
    Api.exportInfomart(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

export const exportWebSaleReport = async sale_id =>
    Api.exportWebSaleReport(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * update a sale
 */
export const updateSale = async (sale_id, data) =>
    Api.updateSale(sale_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale codes for an othanisation
 */
export const getOrganisationSaleCodes = async organisation_id =>
    Api.getOrganisationSaleCodes(organisation_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * associate a sale code to an asset
 */
export const assignSaleCode = async (auctioneer_id, asset_id, data) =>
    Api.assignSaleCode(auctioneer_id, asset_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * publish a sale
 */
export const activate = async sale_id =>
    Api.activate(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sales for a specidi user
 * @param {int} user_id 
 * @param {array} params 
 * @returns 
 */
export const getForUser = async (user_id, params = []) =>
    Api.getForUser(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * delete a collection of sales
 * @param {*} data 
 * @returns 
 */
export const batchDelete = async data =>
    Api.batchDelete(data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * update a collection of sales
 * @param {*} data 
 * @returns 
 */
export const batchUpdate = async data =>
    Api.batchUpdate(data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale realted feature fields for this user
 */
export const getUserFeatureFields = async (user_id, params = []) =>
    Api.getUserFeatureFields(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * add a feature field
 */
export const createField = async data =>
    Api.createField(data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a feature field by id
 * 
 */
export const getFeatureField = async feature_field_id =>
    Api.getFeatureField(feature_field_id)
        .then( _onSuccess )
        .catch( _onFailed )


/**
 * update a feature field
 * @param {int} feature_field_id 
 * @param {obj} data 
 * @returns 
 */
export const updateFeatureField = async (feature_field_id, data) =>
    Api.updateFeatureField(feature_field_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * find available lot numbers
 * @param {*} params 
 * @returns 
 */
export const lotNumberAvailable = async params =>
    Api.lotNumberAvailable(params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get the sale's alignible assets
 * @param {*} sale_id 
 * @param {*} params 
 * @returns 
 */
export const getEligibleAssets = async (params = []) =>
    Api.getEligibleAssets(params)
        .then( _onSuccess )
        .catch( _onFailed )

export const getEligibleSales = async (params = []) =>
    Api.getEligibleSales(params)
    .then( _onSuccess )
        .catch( _onFailed )

/**
 * add a lot an auctioneer sale
 * @param {int} auctioneer_id 
 * @param {int} sale_id 
 * @param {obj} data 
 * @returns 
 */
export const addLotToAuctioneerSale = async (auctioneer_id, sale_id, data) =>
    Api.addLotToAuctioneerSale(auctioneer_id, sale_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get lots
 */
export const getLots = async (params = []) => 
    Api.getLots(params)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * get a sale by id
 * @param {int} sale_id 
 * @returns 
 */
export const getById = async sale_id =>
    Api.getById(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * publish a sale by id
 * @param {int} sale_id
 * @returns
 */
export const publish = async sale_id =>
    Api.publish(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * unpublish a sale by id
 * @param {int} sale_id
 * @returns
 */
export const unpublish = async sale_id =>
    Api.unpublish(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

export const exportBidJS = async sale_id =>
    Api.exportBidJS(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * re-sync a sale code's sale sections
 */
export const syncSaleSectionsToSaleCode = async (sale_code_id, data) =>
    Api.syncSaleSectionsToSaleCode(sale_code_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * delete an auctioneer's sale code
 * @param auctioneer_id
 * @param sale_code_id
 * @returns {Promise<obj>}
 */
export const deleteAuctioneerSaleCode = async (auctioneer_id, sale_code_id) =>
    Api.deleteAuctioneerSaleCode(auctioneer_id, sale_code_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * delete an auctioneer's sale section
 * @param auctioneer_id
 * @param sale_section_id
 * @returns {Promise<obj>}
 */
export const deleteAuctioneerSaleSection = async (auctioneer_id, sale_section_id) =>
    Api.deleteAuctioneerSaleSection(auctioneer_id, sale_section_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * delete an auctioneer's sale type
 * @param auctioneer_id
 * @param sale_type_id
 * @returns {Promise<obj>}
 */
export const deleteAuctioneerSaleType = async (auctioneer_id, sale_type_id) =>
    Api.deleteAuctioneerSaleType(auctioneer_id, sale_type_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * withdraw a lot from a sale
 * @param auctioneer_id
 * @param sale_id
 * @param lot_id
 * @returns {Promise<obj>}
 */
export const withdrawLotFromSale = async (auctioneer_id, sale_id, lot_id) =>
    Api.withdrawLotFromSale(auctioneer_id, sale_id, lot_id)
        .then( _onSuccess )
        .catch( _onFailed )

/**
 * marl a lot as unsold
 * @param auctioneer_id
 * @param sale_id
 * @param lot_id
 * @returns {Promise<obj>}
 */
export const markLotAsUnsold = async (auctioneer_id, sale_id, lot_id) =>
    Api.markLotAsUnsold(auctioneer_id, sale_id, lot_id)
        .then( _onSuccess )
        .catch( _onFailed )

export const exportUserSaleSettings = async (user_id, params) =>
    Api.exportUserSaleSettings(user_id, params)
        .then( _onSuccess )
        .catch( _onFailed )

export const withdrawManyLots = async (sale_id, data) =>
    Api.withdrawManyLots(sale_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

export const markManyAsUnsold = async (sale_id, data) =>
    Api.markManyAsUnsold(sale_id, data)
        .then( _onSuccess )
        .catch( _onFailed )

export const exportSaleJson = async sale_id =>
    Api.exportSaleJson(sale_id)
        .then( _onSuccess )
        .catch( _onFailed )